import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { addDays, addMonths, diffDays } from '@dairy/shared';
import { Card, PageHeader, Segmented, Table, Td, Th } from '@/components/ui';
import { db } from '@/db/db';
import { useHerd, useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { StageBadge } from '@/features/animals/common';

type Filter = 'ALL' | 'OPEN' | 'SERVED' | 'PREGNANT' | 'CALVING60';

/** Screen 14 — Breeding overview (BR-10): active females from 12 months, sorted by next action date. */
export function BreedingOverviewPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const today = useToday();
  const navigate = useNavigate();
  const herd = useHerd(today);
  const reminders = useLiveQuery(() => db.reminders.where('status').equals('OPEN').toArray(), []) ?? [];
  const [filter, setFilter] = useState<Filter>('ALL');
  const rows = herd.rows
    .filter((r) => r.animal.status === 'ACTIVE' && r.animal.sex === 'FEMALE' && today >= addMonths(r.animal.birthDate, 12))
    .map((r) => {
      const next = reminders.filter((x) => x.animalId === r.animal.id).sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))[0];
      return { ...r, next };
    })
    .filter((r) => {
      if (filter === 'ALL') return true;
      if (filter === 'CALVING60') return !!r.repro?.expectedCalving && r.repro.expectedCalving <= addDays(today, 60);
      return r.repro?.status === filter;
    })
    .sort((a, b) => (a.next?.dueDate ?? '9999') .localeCompare(b.next?.dueDate ?? '9999'));
  return (
    <div className="space-y-4">
      <PageHeader title={t('breeding.overviewTitle')} />
      <Segmented
        value={filter}
        onChange={setFilter}
        options={[
          { value: 'ALL', label: t('common.all') },
          { value: 'OPEN', label: t('animals.repro.OPEN') },
          { value: 'SERVED', label: t('animals.repro.SERVED') },
          { value: 'PREGNANT', label: t('animals.repro.PREGNANT') },
          { value: 'CALVING60', label: t('breeding.calving60') },
        ]}
      />
      <Card className="p-2">
        <Table>
          <thead>
            <tr>
              <Th>{t('animals.tag')}</Th>
              <Th>{t('animals.stageLabel')}</Th>
              <Th>{t('breeding.status')}</Th>
              <Th className="text-right">{t('breeding.daysSinceCalving')}</Th>
              <Th>{t('breeding.lastService')}</Th>
              <Th className="text-right">{t('breeding.daysSinceService')}</Th>
              <Th>{t('breeding.expectedCalving')}</Th>
              <Th>{t('breeding.nextAction')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.animal.id} className="cursor-pointer hover:bg-stone-50" onClick={() => navigate(`/animals/${r.animal.id}`)}>
                <Td className="font-semibold">{r.animal.tag}</Td>
                <Td>
                  <StageBadge stage={r.stage} />
                </Td>
                <Td>{r.repro ? t(`animals.repro.${r.repro.status}`) : '—'}</Td>
                <Td className="text-right tabular-nums">{r.repro?.lastCalvingDate ? diffDays(today, r.repro.lastCalvingDate) : '—'}</Td>
                <Td>{f.short(r.repro?.lastServiceDate)}</Td>
                <Td className="text-right tabular-nums">{r.repro?.lastServiceDate ? diffDays(today, r.repro.lastServiceDate) : '—'}</Td>
                <Td>{f.short(r.repro?.expectedCalving)}</Td>
                <Td>{r.next ? `${t(`reminders.type.${r.next.type}`)} · ${f.short(r.next.dueDate)}` : '—'}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}
